/**
 * Explore — discover public SocialScoop feeds.
 *
 * Shows all profiles with feed_is_public = true.
 * Curators appear first, badged.
 * Real-time search by username or full name.
 * Follow / unfollow without leaving the page.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface PublicProfile {
  id: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  curator_tagline: string | null;
  is_curator: boolean;
  is_featured: boolean;
  follower_count: number;
  is_following: boolean;
}

// ── Avatar ────────────────────────────────────────────────────────────────────

const Avatar: React.FC<{ profile: PublicProfile; size?: number }> = ({ profile, size = 44 }) => {
  const initials = (profile.full_name ?? profile.username ?? '?')
    .split(' ')
    .map((w: string) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  if (profile.avatar_url) {
    return (
      <img
        src={profile.avatar_url}
        alt={profile.full_name ?? profile.username ?? 'User'}
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-muted flex items-center justify-center font-semibold text-foreground shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials}
    </div>
  );
};

// ── Profile card ──────────────────────────────────────────────────────────────

interface ProfileCardProps {
  profile: PublicProfile;
  currentUserId: string | undefined;
  onToggleFollow: (profile: PublicProfile) => Promise<void>;
  followingId: string | null;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ profile, currentUserId, onToggleFollow, followingId }) => {
  const isMe = profile.id === currentUserId;
  const hasUsername = Boolean(profile.username);

  return (
    <article className="flex items-start gap-4 p-5 rounded-xl border border-border bg-card hover:shadow-sm transition-shadow">
      {hasUsername ? (
        <Link to={`/u/${profile.username}`} className="shrink-0">
          <Avatar profile={profile} size={44} />
        </Link>
      ) : (
        <Avatar profile={profile} size={44} />
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          {hasUsername ? (
            <Link
              to={`/u/${profile.username}`}
              className="font-semibold text-sm hover:underline underline-offset-4 truncate"
            >
              {profile.full_name ?? `@${profile.username}`}
            </Link>
          ) : (
            <span className="font-semibold text-sm truncate">
              {profile.full_name ?? 'Anonymous'}
            </span>
          )}

          {profile.is_curator && (
            <span className="text-[10px] uppercase tracking-wider border border-border rounded-full px-2 py-0.5 text-muted-foreground shrink-0">
              {profile.is_featured ? '⭐ Founding Curator' : 'Curator'}
            </span>
          )}
        </div>

        {profile.username && (
          <p className="text-xs text-muted-foreground mb-1">@{profile.username}</p>
        )}

        {profile.curator_tagline && (
          <p className="text-xs text-muted-foreground leading-snug line-clamp-2">
            {profile.curator_tagline}
          </p>
        )}

        <p className="text-xs text-muted-foreground mt-1.5 tabular-nums">
          {profile.follower_count.toLocaleString()}{' '}
          {profile.follower_count === 1 ? 'follower' : 'followers'}
        </p>
      </div>

      {!isMe && (
        <Button
          size="sm"
          variant={profile.is_following ? 'outline' : 'default'}
          className="shrink-0 h-8 text-xs px-3"
          onClick={() => onToggleFollow(profile)}
          disabled={followingId === profile.id}
        >
          {followingId === profile.id ? '…' : profile.is_following ? 'Following' : 'Follow'}
        </Button>
      )}
    </article>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

export const Explore: React.FC = () => {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<PublicProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [followingId, setFollowingId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus search on mount
  useEffect(() => { searchRef.current?.focus(); }, []);

  // Fetch all public profiles, enriched with follow status
  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('id, username, full_name, avatar_url, curator_tagline, is_curator, is_featured, follower_count')
        .eq('feed_is_public', true)
        .order('is_featured', { ascending: false })
        .order('is_curator', { ascending: false })
        .order('follower_count', { ascending: false })
        .limit(200);

      if (error) throw error;

      // Get who the current user is following
      let followingSet = new Set<string>();
      if (user?.id) {
        const { data: follows } = await supabase
          .from('curator_follows')
          .select('curator_id')
          .eq('follower_id', user.id);
        followingSet = new Set((follows ?? []).map((f: { curator_id: string }) => f.curator_id));
      }

      setProfiles(
        (profileData ?? []).map((p: PublicProfile) => ({
          ...p,
          is_following: followingSet.has(p.id),
        })),
      );
    } catch (err) {
      console.error('Explore fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  // Toggle follow
  const toggleFollow = async (profile: PublicProfile) => {
    if (!user) { toast.error('Sign in to follow people.'); return; }
    setFollowingId(profile.id);
    try {
      if (profile.is_following) {
        await supabase
          .from('curator_follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('curator_id', profile.id);
        setProfiles((prev) =>
          prev.map((p) =>
            p.id === profile.id
              ? { ...p, is_following: false, follower_count: Math.max(p.follower_count - 1, 0) }
              : p,
          ),
        );
      } else {
        await supabase
          .from('curator_follows')
          .insert({ follower_id: user.id, curator_id: profile.id });
        setProfiles((prev) =>
          prev.map((p) =>
            p.id === profile.id
              ? { ...p, is_following: true, follower_count: p.follower_count + 1 }
              : p,
          ),
        );
      }
    } catch {
      toast.error('Something went wrong. Try again.');
    } finally {
      setFollowingId(null);
    }
  };

  // Client-side search filter
  const filtered = query.trim()
    ? profiles.filter((p) => {
        const q = query.toLowerCase();
        return (
          p.username?.toLowerCase().includes(q) ||
          p.full_name?.toLowerCase().includes(q) ||
          p.curator_tagline?.toLowerCase().includes(q)
        );
      })
    : profiles;

  const curators = filtered.filter((p) => p.is_curator);
  const everyone = filtered.filter((p) => !p.is_curator);

  return (
    <div className="px-6 py-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-light tracking-tight mb-1">Explore</h1>
        <p className="text-sm text-muted-foreground">
          Browse public SocialScoop feeds and follow people whose curation you trust.
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or @username…"
          className="pl-9 h-11"
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-start gap-4 p-5 rounded-xl border border-border bg-card animate-pulse">
              <div className="w-11 h-11 rounded-full bg-muted shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-muted rounded w-32" />
                <div className="h-3 bg-muted rounded w-48" />
                <div className="h-3 bg-muted rounded w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Users className="h-8 w-8 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">
            {query ? `No results for "${query}"` : 'No public profiles yet.'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {query
              ? 'Try a different name or username.'
              : 'Be the first — make your feed public in Settings → Privacy.'}
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {/* Curators section */}
          {curators.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-4">
                Curators
              </div>
              <div className="space-y-3">
                {curators.map((p) => (
                  <ProfileCard
                    key={p.id}
                    profile={p}
                    currentUserId={user?.id}
                    onToggleFollow={toggleFollow}
                    followingId={followingId}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Everyone else */}
          {everyone.length > 0 && (
            <section>
              {curators.length > 0 && (
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-4">
                  Public feeds
                </div>
              )}
              <div className="space-y-3">
                {everyone.map((p) => (
                  <ProfileCard
                    key={p.id}
                    profile={p}
                    currentUserId={user?.id}
                    onToggleFollow={toggleFollow}
                    followingId={followingId}
                  />
                ))}
              </div>
            </section>
          )}

          <p className="text-xs text-muted-foreground text-center pb-4">
            {filtered.length} public {filtered.length === 1 ? 'profile' : 'profiles'} ·{' '}
            Make yours public in{' '}
            <Link to="/dashboard/settings?tab=privacy" className="underline underline-offset-4 hover:text-foreground">
              Settings → Privacy
            </Link>
          </p>
        </div>
      )}
    </div>
  );
};

export default Explore;
